// Scoped checkout handoff. Never put contact details, signatures,
// or Stripe client secrets in the URL; the URL carries only an opaque draft id.
export const CHECKOUT_TTL_MS = 30 * 60 * 1000;
export const CHECKOUT_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
const PREFIX = "cabinCheckout:";
const RECOVERY = "Your saved checkout is missing or expired. Return to date selection, or contact the host for help. If you already submitted payment, check with the host before starting another checkout.";

export type CheckoutForm = {
  name: string; email: string; phone: string;
  addrLine1: string; addrCity: string; addrState: string; addrZip: string;
  message: string; paymentType: "full" | "deposit"; pontoonAddon: boolean;
  marketingOptIn: boolean; discountCode: string;
  discountValid: { type: "percent" | "flat_total"; percent: number; flatTotalAmount: number; codeId: string; redundant?: boolean; message?: string } | null;
};

export type CheckoutPayment = {
  clientSecret: string; bookingId: string; totalDollars: number; taxAmount: number;
  chargeAmount: number; depositAmount: number; cleaningFee: number; extraGuestFee: number;
  processingFee: number; pontoonAddonCost: number; discountAmount: number; discountSource: string;
};

export type CheckoutDraft = {
  id: string; createdAt: number; checkIn: string; checkOut: string;
  adults: number; children: number; infants: number; dogs: number;
  form: CheckoutForm; experimentVariant?: string; quotedTotal: number;
  phase: "details" | "agreement" | "creating" | "payment" | "complete";
  revision?: number;
  signature?: unknown; signatureAt?: number;
  payment?: CheckoutPayment; stripeKey?: string;
};

export async function saveCheckout(draft: CheckoutDraft): Promise<void> {
  const next = { ...draft, revision: (draft.revision ?? 0) + 1 };
  const value = JSON.stringify(next);
  // Signing proof stays tab-scoped. Never persist a signature image or
  // presentation token in localStorage or in the server-side draft copy.
  try {
    window.sessionStorage.setItem("cabinCheckoutStorageProbe", "1");
    if (window.sessionStorage.getItem("cabinCheckoutStorageProbe") !== "1") throw new Error();
    window.sessionStorage.removeItem("cabinCheckoutStorageProbe");
  } catch {
    throw new Error("Your browser could not save checkout details. Stay on this page and allow site storage before continuing, or contact the host.");
  }
  const { signature: _signature, signatureAt: _signatureAt, ...serverCopy } = next;
  const res = await fetch(`/api/checkout-drafts/${encodeURIComponent(draft.id)}`, {
    method: "PUT", credentials: "same-origin", headers: { "Content-Type": "application/json" },
    body: JSON.stringify(serverCopy),
  });
  if (!res.ok) {
    // Keep uncertainty fail-closed; never replace a creating/payment marker
    // with an older pre-payment snapshot after an interrupted request.
    throw new Error((await res.json().catch(() => ({}))).message || "Checkout could not be saved. Stay on this page and try again.");
  }
  if (!draft.revision) {
    const confirmation = await fetch(`/api/checkout-drafts/${encodeURIComponent(draft.id)}`, { credentials: "same-origin", cache: "no-store" });
    if (!confirmation.ok) throw new Error("Your browser must allow this site's session cookie to continue. Stay on this page or contact the host.");
  }
  window.sessionStorage.setItem(PREFIX + draft.id, value);
  draft.revision = next.revision;
}

function validateCheckout(draft: CheckoutDraft | null, id: string | null): CheckoutDraft {
  try {
    if (!id || !/^[a-zA-Z0-9-]{1,80}$/.test(id)) throw new Error();
    if (!draft || draft.id !== id || !Number.isFinite(draft.createdAt) ||
        draft.createdAt > Date.now() || Date.now() - draft.createdAt >= CHECKOUT_DRAFT_TTL_MS ||
        !/^\d{4}-\d{2}-\d{2}$/.test(draft.checkIn) || !/^\d{4}-\d{2}-\d{2}$/.test(draft.checkOut) ||
        !Number.isFinite(Date.parse(draft.checkIn)) || draft.checkOut <= draft.checkIn ||
        !draft.form || !["name", "email", "phone", "addrLine1", "addrCity", "addrState", "addrZip", "message", "discountCode"].every(field =>
          typeof draft.form[field as keyof CheckoutForm] === "string") ||
        !["full", "deposit"].includes(draft.form.paymentType) ||
        typeof draft.form.pontoonAddon !== "boolean" || typeof draft.form.marketingOptIn !== "boolean" ||
        (draft.revision !== undefined && (!Number.isInteger(draft.revision) || draft.revision < 0)) ||
        ![draft.adults, draft.children, draft.infants, draft.dogs].every(Number.isInteger) ||
        draft.adults < 1 || draft.children < 0 || draft.infants < 0 || draft.dogs < 0 ||
        !["details", "agreement", "creating", "payment", "complete"].includes(draft.phase) ||
        (draft.phase === "payment" && (!draft.payment?.clientSecret || !draft.payment.bookingId || !draft.stripeKey))) {
      throw new Error();
    }
    return draft;
  } catch {
    throw new Error(RECOVERY);
  }
}

export function readCheckout(id: string | null): CheckoutDraft {
  try {
    const value = id && window.sessionStorage.getItem(PREFIX + id);
    return validateCheckout(value ? JSON.parse(value) : null, id);
  } catch { throw new Error(RECOVERY); }
}

export async function loadCheckout(id: string | null): Promise<CheckoutDraft> {
  let tab: CheckoutDraft | undefined;
  try { tab = readCheckout(id); } catch { /* The server may have recoverable context. */ }
  let server: CheckoutDraft | undefined;
  if (id && /^[a-zA-Z0-9-]{1,80}$/.test(id)) {
    try {
      const res = await fetch(`/api/checkout-drafts/${encodeURIComponent(id)}`, { credentials: "same-origin", cache: "no-store" });
      if (res.ok) server = validateCheckout(await res.json(), id);
    } catch { /* A valid tab snapshot can still show recovery safely. */ }
  }
  // Validate each source independently, then prefer the newest valid revision.
  // At equal revision the tab is the only copy allowed to contain signing proof.
  const draft = tab && (!server || (tab.revision ?? 0) >= (server.revision ?? 0)) ? tab : server;
  if (!draft) throw new Error(RECOVERY);
  try { window.sessionStorage.setItem(PREFIX + draft.id, JSON.stringify(draft)); } catch { /* Signing reports blocked storage before navigating. */ }
  return draft;
}

// A legacy link has no id. Offer an explicit choice instead of silently
// signing/paying whichever stay happened to be saved most recently.
export async function listSavedCheckouts(): Promise<CheckoutDraft[]> {
  const ids = new Set<string>();
  const valid = new Map<string, CheckoutDraft>();
  try {
    const res = await fetch("/api/checkout-drafts/", { credentials: "same-origin", cache: "no-store" });
    if (res.ok) for (const value of await res.json()) {
      try { const draft = validateCheckout(value, value.id); valid.set(draft.id, draft); } catch { /* Ignore malformed/expired copies, not other stays. */ }
    }
  } catch { /* Still offer valid tab-scoped drafts. */ }
  try {
    for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(PREFIX)) ids.add(key.slice(PREFIX.length));
    }
  } catch { /* Blocked storage. */ }
  for (const id of ids) {
    try {
      const tab = readCheckout(id);
      if ((tab.revision ?? 0) >= (valid.get(id)?.revision ?? -1)) valid.set(id, tab);
    }
    catch {
      try { window.sessionStorage.removeItem(PREFIX + id); } catch { /* Blocked store. */ }
    }
  }
  return [...valid.values()].sort((a, b) => b.createdAt - a.createdAt);
}

export function hasCheckoutSignature(draft: CheckoutDraft): boolean {
  const age = Date.now() - Number(draft.signatureAt);
  return Boolean(draft.signature && age >= 0 && age < CHECKOUT_TTL_MS);
}

export function checkoutReturnUrl(id: string): string {
  return `/?checkoutResume=${encodeURIComponent(id)}#booking`;
}

// Do not follow arbitrary returnTo URLs (including protocol-relative URLs).
// Custom payment tokens must be preserved exactly, not replaced by a homepage.
export function safePaymentReturn(value: string | null): string | null {
  if (!value || !value.startsWith("/") || value.startsWith("//") || /[\\\u0000-\u0020]/.test(value)) return null;
  const url = new URL(value, window.location.origin);
  if (url.origin !== window.location.origin) return null;
  if (!/^\/cabin-payment\/[a-zA-Z0-9-]+$/.test(url.pathname) && url.pathname !== "/my-booking") return null;
  return url.pathname + url.search + url.hash;
}
